<script setup lang="ts">
import {
  ButtonIcon,
  CustomLink,
  LinkColor,
  LinkIcon,
  LinkIconLocation,
  LinkSize
} from "@frontiers-vue3/public-pages";
import type { IRTSidePanel } from "./IRTSidePanel";
import type { IJournal } from "~/types";
import { useShareInSocialNetwork } from "~/composables/useShareInSocialNetwork";

import RTSidePanelImpact from "./components/RTSidePanelImpact/RTSidePanelImpact.vue";
import RTSidepanelParticipatingJournals from "./components/RTSidepanelParticipatingJournals/RTSidepanelParticipatingJournals.vue";
import RTSidePanelSubmitManuscript from "./components/RTSidePanelSubmitManuscript/RTSidePanelSubmitManuscript.vue";

const props = defineProps<IRTSidePanel>();

const config = useRuntimeConfig();
const mainStore = useMain();
const { isImpactMetricsVisible, showRtImpactTab, minimumRtDownloadsCount } =
  useEnvironmentConfigStore().getConfig();

const sanitize = inject("sanitize") as (html: string) => string;

const participatingJournals = ref<IJournal[]>(
  mainStore.currentResearchTopic?.journals || []
);
const displayParticipatingJournals = ref<boolean>(
  useEnvBoolean(config.public.isParticipatingJournalsVisible) &&
    participatingJournals.value.length > 0
);

const rtSponsorship = mainStore.rtSponsorship;

const showSponsorshipBlock = computed(
  (): boolean =>
    !!(
      Boolean(config.public.showRtSponsorship) &&
      rtSponsorship &&
      Object.values(rtSponsorship).length > 0
    )
);

const displayImpactSection = computed((): boolean => {
  return (
    isImpactMetricsVisible &&
    showRtImpactTab &&
    Object.values(props.impactNumbers).some(
      (impactNumber) => impactNumber > minimumRtDownloadsCount
    )
  );
});

const hideImage = (e: Event): void => {
  if (!e.target) return;
  (e.target as HTMLElement).setAttribute("style", "display: none");
};

const { getSocialShareLink } = useShareInSocialNetwork();
</script>
<template>
  <div class="RtSidePanel">
    <div class="RtSidePanel__Section RtSidePanel__Section--ShareIcons">
      <span>Share on</span>
      <div>
        <ButtonIcon
          label="Share on X"
          show-tooltip
          icon="TwitterX"
          :href="getSocialShareLink('X')"
          target="_blank">
        </ButtonIcon>
        <ButtonIcon
          label="Share on LinkedIn"
          show-tooltip
          icon="Linkedin"
          :href="getSocialShareLink('LinkedIn')"
          target="_blank" />
        <ButtonIcon
          label="Share on Facebook"
          show-tooltip
          icon="Facebook"
          :href="getSocialShareLink('Facebook')"
          target="_blank" />
      </div>
    </div>
    <!-- Participating journals -->
    <RTSidepanelParticipatingJournals
      v-if="displayParticipatingJournals"
      :journals="participatingJournals" />

    <!-- Submit -->
    <RTSidePanelSubmitManuscript v-if="isSubmitManuscriptEnabled" />

    <!-- Sponsorship -->
    <section
      v-if="showSponsorshipBlock"
      class="RtSidePanel__Section Sponsorship">
      <p class="h4">Sponsorship</p>

      <p
        v-if="rtSponsorship && rtSponsorship.message"
        class="Sponsorship__Content"
        v-html="sanitize(rtSponsorship.message)"></p>
      <div>
        <img
          v-if="rtSponsorship?.imageUrl"
          class="Sponsorship__Image"
          :src="rtSponsorship?.imageUrl"
          @error="hideImage" />

        <CustomLink
          v-if="rtSponsorship?.link"
          class="Sponsorship__Link"
          :link="{
            text: 'Search for my institution',
            url: rtSponsorship?.link,
            target: '_blank'
          }"
          :size="LinkSize.Medium"
          :color="LinkColor.Maincolor"
          :icon="LinkIcon.ChevronRight"
          :icon-location="LinkIconLocation.Right" />
      </div>
    </section>

    <!-- Impact -->
    <RTSidePanelImpact
      v-if="displayImpactSection"
      :impact-numbers="impactNumbers" />
  </div>
</template>

<style lang="scss">
.RtSidePanel {
  background-color: $grey10;
  width: var(--layout-col-5);
  margin-left: 0;

  @media #{$break-md} {
    width: 100%;
    margin-bottom: 64px;
  }

  @media #{$break-md} {
    margin-top: 48px;
  }

  @media #{$break-xs} {
    margin: 24px 0;
  }

  &__Section {
    position: relative;
    padding: 24px 0;
    border-bottom: 1px solid $grey40;
    color: $grey70;

    &:first-child {
      border-top: 1px solid $grey40;
    }

    &__Description {
      @include f-16-300;

      margin-top: 8px;
      margin-bottom: 24px;
    }

    &__Title {
      @include f-18;
      font-weight: 400;
      margin-top: 0;
    }

    ul {
      p {
        @include f-16;
        font-weight: 400;
        margin-bottom: 0;
      }
    }

    &__AuthorList {
      margin-top: 16px;

      li {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;

        img {
          height: 32px;
          width: 32px;
          border-radius: 40px;
          overflow: hidden;
        }
      }
    }

    &__Link {
      @include f-16;
      font-weight: 400;
      position: absolute;
      bottom: -10px;
      right: 0;
      padding-left: 16px;
      color: var(--maincolor50);
      background-color: $grey10;
    }

    &:has(.RtSidePanel__Section__Link) {
      padding-bottom: 33px;
    }

    &--ShareIcons {
      @include f-18-500;
      display: flex;
      align-items: center;
      gap: 20px;

      @media #{$break-laptop-sm} {
        gap: 12px;
      }

      @media #{$break-md} {
        gap: 20px;
      }

      div {
        display: flex;
        gap: 12px;

        @media #{$break-laptop-sm} {
          gap: 2px;
        }

        @media #{$break-md} {
          gap: 12px;
        }
      }
    }
  }

  .Sponsorship {
    p {
      @include f-18-500;
      color: $grey70;
      margin: 0 0px 24px 0;

      & + .Sponsorship__Content {
        @include f-16-300;
        margin: 0 0 24px 0;
      }
    }

    img {
      border-radius: 8px;
      border: 1px solid $grey30;
      margin-bottom: 24px;
      max-inline-size: 100%;
      max-block-size: 100%;
    }

    &__Link {
      display: flex;
      @include f-16-500;
      color: var(--maincolor50);
      gap: 8px;
      width: max-content;
      svg {
        margin-top: 1px;
      }
    }
  }
}
</style>
